import { ParseError } from "@/repositories/shared/errors/parseError";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

import dayjs from "dayjs";
import {
  INewRouteApplicationDetail,
  INewRouteReceiver,
  INewRouteReply,
  NewRouteApplicationDetail,
} from "@/domain/entities/bbs";
import {
  HttpNewRouteApplicationDetail,
  HttpNewRouteReceiver,
  HttpNewRouteReply,
  INewRouteApplicationDetailParser,
} from "../bbsRepository.types";

function parseReceiver(data: HttpNewRouteReceiver): INewRouteReceiver {
  const receiver: INewRouteReceiver = {
    routeApplicationUid: data.talkLineUid,
    userUid: data.memberUid,
    userName: data.memberName,
    employeeNo: data.employeeNo,
    phoneEnc: data.phoneEnc,
    savedTs: data.savedTs ? dayjs(data.savedTs).format("YYYY-MM-DD HH:mm") : "",
  };
  return receiver;
}

function toHttpReceiver(domainData: INewRouteReceiver): HttpNewRouteReceiver {
  const receiver: HttpNewRouteReceiver = {
    talkLineUid: domainData.routeApplicationUid,
    memberUid: domainData.userUid,
    memberName: domainData.userName,
    employeeNo: domainData.employeeNo,
    phoneEnc: domainData.phoneEnc,
    savedTs: dayjs(domainData.savedTs).format(),
  };
  return receiver;
}

function parseReply(data: HttpNewRouteReply): INewRouteReply {
  const reply: INewRouteReply = {
    uid: data.uid,
    parentUid: data.parentUid,
    serviceUid: data.serviceUid,
    userUid: data.userUid,
    typeCode: data.typeCode,
    typeName: data.typeName,
    useBeginDtm: dayjs(data.useBeginDtm).format("YYYY-MM-DD HH:mm"),
    useEndDtm: data.useEndDtm
      ? dayjs(data.useEndDtm).format("YYYY-MM-DD HH:mm")
      : undefined,
    bodyText: data.bodyText,
    displayYn: data.displayYn,
    statusCode: data.statusCode,
    statusName: data.statusName,
    lineTypeCode: data.lineTypeCode,
    createdTs: dayjs(data.createdTs).format("YYYY-MM-DD HH:mm:ss"),
    updatedTs: data.updatedTs
      ? dayjs(data.updatedTs).format("YYYY-MM-DD HH:mm:ss")
      : undefined,
    savedTs: dayjs(data.savedTs).format("YYYY-MM-DD HH:mm:ss"),
  };
  return reply;
}

function toHttpReply(data: INewRouteReply): HttpNewRouteReply {
  const reply: HttpNewRouteReply = {
    uid: data.uid,
    parentUid: data.parentUid,
    serviceUid: data.serviceUid,
    userUid: data.userUid,
    typeCode: data.typeCode,
    typeName: data.typeName,
    useBeginDtm: dayjs(data.useBeginDtm).format(),
    useEndDtm: dayjs(data.useEndDtm).format(),
    bodyText: data.bodyText,
    displayYn: data.displayYn,
    statusCode: data.statusCode,
    statusName: data.statusName,
    lineTypeCode: data.lineTypeCode,
    createdTs: dayjs(data.createdTs).format(),
    updatedTs: dayjs(data.updatedTs).format(),
    savedTs: dayjs(data.savedTs).format(),
  };
  return reply;
}

function formattingTime(time: number) {
  return time?.toString().padStart(2, "0") ?? "00";
}

function parseTimeStrToNumber(time: string) {
  return isNaN(Number(time)) ? 0 : Number(time);
}

@injectable()
export class NewRouteApplicationDetailParser
  implements INewRouteApplicationDetailParser
{
  constructor() {}

  toDomain(
    data: HttpNewRouteApplicationDetail
  ): Result<INewRouteApplicationDetail, ParseError> {
    try {
      const address = data.roadAddress?.length
        ? data.roadAddress
        : data.dongAddress ?? "";
      const detailAddress = data.detailAddress?.length
        ? ` ${data.detailAddress}`
        : "";
      const newRouteApplicationDetail: INewRouteApplicationDetail = {
        uid: data.uid,
        title: `${address}${detailAddress}`,
        bodyText: data.bodyText,
        memberUid: data.memberUid,
        memberName: data.memberName,
        employeeNo: data.employeeNo,
        serviceUid: data.serviceUid,
        campusCode: data.campusCode,
        campusName: data.campusName,
        workTimeCode: data.workTimeCode,
        workTimeName: data.workTimeName,
        talkLineWeekdayCode: data.talkLineWeekdayCode,
        talkLineWeekdayName: data.talkLineWeekdayName,
        lineDirectionCode: data.lineDirectionCode,
        lineDirectionName: data.lineDirectionName,
        displayYn: data.displayYn,
        roadAddress: data.roadAddress,
        detailAddress: data.detailAddress,
        dongAddress: data.dongAddress,
        lat: data.lat,
        lng: data.lng,
        receiverCount: data.receiverCount,
        receivers: data.receivers?.map(parseReceiver) ?? [],
        replies: data.replies?.map(parseReply) ?? [],
        statusCode: data.statusCode,
        statusName: data.statusName,
        talkTypeCode: data.talkTypeCode,
        useBeginDtm: dayjs(data.useBeginDtm).format("YYYY-MM-DD HH:mm"),
        useEndDtm: data.useEndDtm
          ? dayjs(data.useEndDtm).format("YYYY-MM-DD HH:mm")
          : undefined,
        savedTs: dayjs(data.savedTs).format("YYYY-MM-DD HH:mm:ss"),
        updatedTs: data.updatedTs
          ? dayjs(data.updatedTs).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        createdTs: dayjs(data.createdTs).format("YYYY-MM-DD HH:mm:ss"),
      };

      return ok(new NewRouteApplicationDetail(newRouteApplicationDetail));
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }

  fromDomain(
    domainData: INewRouteApplicationDetail
  ): HttpNewRouteApplicationDetail | ParseError {
    try {
      const newRouteApplicationDetail: HttpNewRouteApplicationDetail = {
        uid: domainData.uid,
        bodyText: domainData.bodyText,
        memberUid: domainData.memberUid,
        memberName: domainData.memberName,
        employeeNo: domainData.employeeNo,
        serviceUid: domainData.serviceUid,
        campusCode: domainData.campusCode,
        campusName: domainData.campusName,
        workTimeCode: domainData.workTimeCode,
        workTimeName: domainData.workTimeName,
        talkLineWeekdayCode: domainData.talkLineWeekdayCode,
        talkLineWeekdayName: domainData.talkLineWeekdayName,
        lineDirectionCode: domainData.lineDirectionCode,
        lineDirectionName: domainData.lineDirectionName,
        displayYn: domainData.displayYn,
        roadAddress: domainData.roadAddress,
        detailAddress: domainData.detailAddress,
        dongAddress: domainData.dongAddress,
        lat: domainData.lat,
        lng: domainData.lng,
        receiverCount: domainData.receiverCount,
        receivers: domainData.receivers?.map(toHttpReceiver) ?? [],
        replies: domainData.replies?.map(toHttpReply) ?? [],
        statusCode: domainData.statusCode,
        statusName: domainData.statusName,
        talkTypeCode: domainData.talkTypeCode,
        useBeginDtm: dayjs(domainData.useBeginDtm).format(),
        useEndDtm: dayjs(domainData.useEndDtm).format(),
      };

      return newRouteApplicationDetail;
    } catch (error: any) {
      return new ParseError(error.message);
    }
  }
}
